import { queryOptions } from "@tanstack/react-query";

import { store, userAtom, zitadelOrgAtom } from "~/atoms";
import { createAxiosRequest } from "~/hooks/useApiQuery";
import { queryClient } from "~/lib/queryClient";
import type { Org } from "~/utils/Types";
import type {
  AccessLens,
  AccessRole,
  FeatureShape,
  ScopeValues,
} from "~/utils/effectiveAccess";
import { getOrgLocation, getOrgShortName } from "~/utils/orgRole";

import { FEATURES_QUERY_KEY, ME_ACCESS_QUERY_KEY } from "./queryKeys";

/** `GET /me/access`: what the caller sees at one org and location. */
export interface AccessPayload {
  org: string;
  location: string;
  defaultRole: string | null;
  roles: AccessRole[];
  lens: AccessLens;
  /** Scoped column values per scoped dataset the caller holds here. */
  scope: Partial<Record<string, ScopeValues>>;
  /** Datasets whose identity scope resolved to nothing at this location. */
  scopeEmpty: Partial<Record<string, boolean>>;
}

export interface AccessAt {
  org: string;
  location: string;
}

export const FEATURES_PATH = "/features";
export const ME_ACCESS_PATH = "/me/access";

// The server folds case itself; folding here too keeps the query key stable
// when the same place arrives spelled differently.
export const accessAtOf = (org: NonNullable<Org>): AccessAt => ({
  org: getOrgShortName(org).toLowerCase(),
  location: getOrgLocation(org).toLowerCase(),
});

const request = <T>(path: string, params?: AccessAt): Promise<T> => {
  const user = store.get(userAtom);
  return createAxiosRequest<T>({
    config: { method: "get", params },
    path,
    transformers: [],
    user: user ? { access_token: user.access_token } : null,
    zitadelOrg: store.get(zitadelOrgAtom),
  });
};

export const featuresQuery = () =>
  queryOptions({
    queryKey: FEATURES_QUERY_KEY,
    queryFn: () => request<FeatureShape[]>(FEATURES_PATH),
    // The registry changes only with a deploy, which ships a new bundle.
    staleTime: Infinity,
    retry: false,
  });

export const meAccessQuery = (at: AccessAt) =>
  queryOptions({
    queryKey: [...ME_ACCESS_QUERY_KEY, at.org, at.location],
    queryFn: () => request<AccessPayload>(ME_ACCESS_PATH, at),
    // Held for the page load. Org selection refetches it, a refused request
    // invalidates it, and nothing persists it across loads, so a new build
    // never boots on a payload an older one fetched.
    staleTime: Infinity,
    retry: false,
  });

/**
 * Fetch both reads afresh for a place the person just selected, so the first
 * page after login or an org switch never renders on an older answer. Never
 * throws: a failed read is the access gate's to show.
 */
export const prefetchAccess = async (org: NonNullable<Org>): Promise<void> => {
  await Promise.all([
    queryClient.prefetchQuery(featuresQuery()),
    queryClient.prefetchQuery({
      ...meAccessQuery(accessAtOf(org)),
      staleTime: 0,
    }),
  ]);
};
